import React, { useState, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { usePunishment } from '../../contexts/PunishmentContext';
import { findLinkedUsers } from '../../utils/linkingUtils';
import {
    ShieldAlert,
    Users,
    Link2,
    Search,
    Calendar,
    User,
    Mail,
    Phone,
    Globe,
    ChevronRight,
    AlertTriangle,
    Eye,
    Ban
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const Blacklist = () => {
    const { allUsers, refreshAllUsers } = useAuth();
    const { getAllPunishments, getActivePunishments, refreshPunishments } = usePunishment();
    const [selectedUser, setSelectedUser] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdated, setLastUpdated] = useState(new Date().toLocaleTimeString('pt-BR'));
    const [isRefreshing, setIsRefreshing] = useState(false);

    // Find all users with active "Blacklist de Tickets" punishment
    const blacklistedUsers = useMemo(() => {
        const allPunishments = getAllPunishments();
        const activeTicketBans = allPunishments.filter(p =>
            p.punishmentType === 'Blacklist de Tickets' &&
            (p.status === 'active' || p.status === 'under_review')
        );

        // Get unique user IDs
        const uniqueUserIds = [...new Set(activeTicketBans.map(p => p.userId))];

        // Map to user objects with punishment info
        return uniqueUserIds.map(userId => {
            const user = allUsers[userId] || { discordId: userId, name: 'Usuário Desconhecido' };
            const punishment = activeTicketBans.find(p => p.userId === userId);
            return {
                ...user,
                punishment
            };
        }).filter(u => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            return (
                u.name?.toLowerCase().includes(term) ||
                u.discordId?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.phone?.toLowerCase().includes(term)
            );
        });
    }, [getAllPunishments, allUsers, searchTerm]);

    // Find linked users for selected user
    const linkedUsers = useMemo(() => {
        if (!selectedUser) return [];

        // Use capturedData from punishment as fallback if user data is missing
        const fallbackData = selectedUser.punishment?.capturedData || {
            ip: selectedUser.ip,
            email: selectedUser.email,
            phone: selectedUser.phone
        };

        return findLinkedUsers(selectedUser.discordId, allUsers, fallbackData);
    }, [selectedUser, allUsers]);

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await Promise.all([
                refreshAllUsers(),
                refreshPunishments()
            ]);
            setLastUpdated(new Date().toLocaleTimeString('pt-BR'));
        } catch (error) {
            console.error("Error refreshing blacklist data:", error);
        } finally {
            setIsRefreshing(false);
        }
    };

    return (
        <div className="p-6 space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-red-500/20 to-orange-500/20 border border-red-500/30">
                        <ShieldAlert className="h-6 w-6 text-red-400" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Blacklist</h1>
                        <p className="text-gray-400 text-sm">Usuários com tickets bloqueados e perfis vinculados</p>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-right">
                        <p className="text-[10px] text-gray-500 uppercase font-bold tracking-wider">Última atualização</p>
                        <p className="text-xs text-gray-400 font-mono">{lastUpdated}</p>
                    </div>
                    <Button
                        variant="outline"
                        size="icon"
                        className={`bg-gray-900/50 border-gray-800 hover:bg-gray-800 transition-all ${isRefreshing ? 'animate-spin' : ''}`}
                        onClick={handleRefresh}
                        disabled={isRefreshing}
                    >
                        <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </Button>
                    <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2">
                        <Ban className="h-5 w-5 text-red-400" />
                        <span className="text-red-300 font-semibold">{blacklistedUsers.length}</span>
                        <span className="text-gray-400 text-sm">usuário(s) bloqueado(s)</span>
                    </div>
                </div>
            </div>

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                <Input
                    placeholder="Buscar por nome, Discord ID, email ou telefone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 bg-gray-900/50 border-gray-700 text-white placeholder:text-gray-500"
                />
            </div>

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Blocked Users List */}
                <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
                    <div className="p-4 border-b border-gray-800 bg-gray-900/80">
                        <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-red-400" />
                            <h2 className="font-semibold text-white">Perfis Bloqueados</h2>
                        </div>
                    </div>
                    <div className="divide-y divide-gray-800 max-h-[600px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                        {blacklistedUsers.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                                <ShieldAlert className="h-12 w-12 mx-auto mb-3 opacity-50" />
                                <p>Nenhum usuário na Blacklist</p>
                            </div>
                        ) : (
                            blacklistedUsers.map((user) => (
                                <div
                                    key={user.discordId}
                                    onClick={() => setSelectedUser(user)}
                                    className={`p-4 cursor-pointer transition-all duration-200 hover:bg-gray-800/50 flex items-center justify-between group ${selectedUser?.discordId === user.discordId ? 'bg-red-500/10 border-l-2 border-red-500' : ''
                                        }`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white font-bold">
                                            {user.avatar ? (
                                                <img
                                                    src={`https://cdn.discordapp.com/avatars/${user.discordId}/${user.avatar}.png`}
                                                    alt={user.name}
                                                    className="w-full h-full rounded-full object-cover"
                                                />
                                            ) : (
                                                user.name?.charAt(0)?.toUpperCase() || '?'
                                            )}
                                        </div>
                                        <div>
                                            <p className="font-medium text-white">{user.name || 'Usuário Desconhecido'}</p>
                                            <p className="text-xs text-gray-500">ID: {user.discordId}</p>
                                        </div>
                                    </div>
                                    <ChevronRight className={`h-5 w-5 text-gray-600 transition-transform group-hover:text-red-400 ${selectedUser?.discordId === user.discordId ? 'rotate-90 text-red-400' : ''
                                        }`} />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* User Details & Linked Profiles */}
                <div className="space-y-6">
                    {/* User Profile Card */}
                    {selectedUser ? (
                        <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
                            <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-red-500/10 to-orange-500/10">
                                <div className="flex items-center gap-2">
                                    <User className="h-5 w-5 text-red-400" />
                                    <h2 className="font-semibold text-white">Perfil Bloqueado</h2>
                                </div>
                            </div>
                            <div className="p-5 space-y-4">
                                {/* Avatar & Name */}
                                <div className="flex items-center gap-4">
                                    <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-red-500/20">
                                        {selectedUser.avatar ? (
                                            <img
                                                src={`https://cdn.discordapp.com/avatars/${selectedUser.discordId}/${selectedUser.avatar}.png`}
                                                alt={selectedUser.name}
                                                className="w-full h-full rounded-xl object-cover"
                                            />
                                        ) : (
                                            selectedUser.name?.charAt(0)?.toUpperCase() || '?'
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-bold text-white">{selectedUser.name}</h3>
                                        <p className="text-gray-400 text-sm">Discord ID: {selectedUser.discordId}</p>
                                    </div>
                                </div>

                                {/* Info Grid */}
                                <div className="grid grid-cols-2 gap-3 mt-4">
                                    <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                                            <Mail className="h-3 w-3" />
                                            Email
                                        </div>
                                        <p className="text-white text-sm font-medium truncate">
                                            {selectedUser.email || selectedUser.punishment?.capturedData?.email || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                                            <Phone className="h-3 w-3" />
                                            Telefone
                                        </div>
                                        <p className="text-white text-sm font-medium">
                                            {selectedUser.phone || selectedUser.punishment?.capturedData?.phone || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                                            <Globe className="h-3 w-3" />
                                            IP
                                        </div>
                                        <p className="text-white text-sm font-medium font-mono">
                                            {selectedUser.ip || selectedUser.punishment?.capturedData?.ip || 'N/A'}
                                        </p>
                                    </div>
                                    <div className="p-3 rounded-lg bg-gray-800/50 border border-gray-700">
                                        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
                                            <Calendar className="h-3 w-3" />
                                            Bloqueado em
                                        </div>
                                        <p className="text-white text-sm font-medium">{formatDate(selectedUser.punishment?.appliedAt)}</p>
                                    </div>
                                </div>

                                {/* Punishment Reason */}
                                {selectedUser.punishment && (
                                    <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 mt-4">
                                        <div className="flex items-center gap-2 text-red-400 text-sm font-medium mb-2">
                                            <AlertTriangle className="h-4 w-4" />
                                            Motivo do Bloqueio
                                        </div>
                                        <p className="text-gray-300 text-sm">{selectedUser.punishment.publicReason}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="bg-gray-900/50 rounded-xl border border-gray-800 p-8 text-center">
                            <Eye className="h-12 w-12 mx-auto mb-3 text-gray-600" />
                            <p className="text-gray-500">Selecione um usuário para ver detalhes</p>
                        </div>
                    )}

                    {/* Linked Profiles */}
                    {selectedUser && (
                        <div className="bg-gray-900/50 rounded-xl border border-gray-800 overflow-hidden">
                            <div className="p-4 border-b border-gray-800 bg-gradient-to-r from-orange-500/10 to-yellow-500/10">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Link2 className="h-5 w-5 text-orange-400" />
                                        <h2 className="font-semibold text-white">Perfis Vinculados</h2>
                                    </div>
                                    <span className="text-xs bg-orange-500/20 text-orange-300 px-2 py-1 rounded-full">
                                        {linkedUsers.length} encontrado(s)
                                    </span>
                                </div>
                            </div>
                            <div className="divide-y divide-gray-800 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
                                {linkedUsers.length === 0 ? (
                                    <div className="p-6 text-center text-gray-500">
                                        <Link2 className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">Nenhum perfil vinculado encontrado</p>
                                    </div>
                                ) : (
                                    linkedUsers.map((linked) => (
                                        <div key={linked.discordId} className="p-4 hover:bg-gray-800/30 transition-colors">
                                            {/* User Header */}
                                            <div className="flex items-center justify-between mb-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-yellow-500 flex items-center justify-center text-white font-bold">
                                                        {linked.avatar ? (
                                                            <img
                                                                src={`https://cdn.discordapp.com/avatars/${linked.discordId}/${linked.avatar}.png`}
                                                                alt={linked.name}
                                                                className="w-full h-full rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            linked.name?.charAt(0)?.toUpperCase() || '?'
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-white">{linked.name || 'Usuário Desconhecido'}</p>
                                                        <p className="text-xs text-gray-500">ID: {linked.discordId}</p>
                                                    </div>
                                                </div>
                                                <div className="flex gap-1 flex-wrap justify-end">
                                                    {linked.linkReason?.map((reason, idx) => (
                                                        <span
                                                            key={idx}
                                                            className={`text-xs px-2 py-1 rounded-full ${reason === 'IP'
                                                                ? 'bg-red-500/20 text-red-300'
                                                                : reason === 'Email'
                                                                    ? 'bg-blue-500/20 text-blue-300'
                                                                    : 'bg-green-500/20 text-green-300'
                                                                }`}
                                                        >
                                                            {reason}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                            {/* User Details Grid */}
                                            <div className="grid grid-cols-3 gap-2 mt-2 pl-13">
                                                <div className="p-2 rounded bg-gray-800/50 border border-gray-700/50">
                                                    <div className="flex items-center gap-1 text-gray-500 text-xs mb-0.5">
                                                        <Mail className="h-3 w-3" />
                                                        Email
                                                    </div>
                                                    <p className={`text-xs font-medium truncate ${linked.linkReason?.includes('Email')
                                                        ? 'text-blue-300'
                                                        : 'text-gray-400'
                                                        }`}>
                                                        {linked.email || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="p-2 rounded bg-gray-800/50 border border-gray-700/50">
                                                    <div className="flex items-center gap-1 text-gray-500 text-xs mb-0.5">
                                                        <Phone className="h-3 w-3" />
                                                        Telefone
                                                    </div>
                                                    <p className={`text-xs font-medium ${linked.linkReason?.includes('Telefone')
                                                        ? 'text-green-300'
                                                        : 'text-gray-400'
                                                        }`}>
                                                        {linked.phone || 'N/A'}
                                                    </p>
                                                </div>
                                                <div className="p-2 rounded bg-gray-800/50 border border-gray-700/50">
                                                    <div className="flex items-center gap-1 text-gray-500 text-xs mb-0.5">
                                                        <Globe className="h-3 w-3" />
                                                        IP
                                                    </div>
                                                    <p className={`text-xs font-medium font-mono ${linked.linkReason?.includes('IP')
                                                        ? 'text-red-300'
                                                        : 'text-gray-400'
                                                        }`}>
                                                        {linked.ip || 'N/A'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Blacklist;
